import React, { useState } from 'react';
import supabase from '../../helper/supabaseClient';
import { useUserProfile } from '../../helper/UserProfile';
import { useRevalidationData } from '../../helper/UserRevalidationProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logo from '../../assets/logo/tight1664logo.png'
import MobileRevalNav from '../mobile/MobileRevalNav';


function FeedbackLog() {
    const { profile } = useUserProfile();

    const { feedback, refresh } = useRevalidationData();

    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [date, setDate] = useState("");
    const [source, setSource] = useState("");
    const [content, setContent] = useState("");
    const [title, setTitle] = useState("");

    const handleFileSelectAndUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !profile?.id) {
            setError('No file or user ID');
            return;
        }

        setUploading(true);
        const filePath = `${profile.id}/${file.name}`;

        const { error: uploadError } = await supabase.storage
            .from('practice-feedback')
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: true,
            });

        if (uploadError) {
            console.error(uploadError);
            setError('Upload failed. Please try again.');
        } else {
            setMessage('File uploaded successfully!');
            setError("");

            await supabase.from('practice_feedback').insert([
                {
                    user_id: profile.id,
                    type_of_feedback: 'Upload',
                    title: file.name,
                    file_path: filePath,
                }
            ]);

            await supabase.from('revalidation_documents').insert([
                {
                    user_id: profile.id,
                    document_type: 'Feedback',
                    file_path: filePath,
                    upload_date: new Date(),
                    bucket: "practice-feedback"
                }
            ]);

            await refresh();
        }

        setUploading(false);
    };

    const handleButtonClick = () => {
        document.getElementById('fileInput').click();
    };

    //Verbal Feedback
    const submitVerbalFeedback = async (e) => {
        e.preventDefault();
        if (!profile?.id || !title) {
            setError("User ID and title are required.");
            return;
        }

        const fileName = `${title}.pdf`;
        const filePath = `${profile.id}/${fileName}`;
        
        

        // Insert DB record
        const { error: insertError } = await supabase.from('practice_feedback').insert({
            user_id: profile.id,
            date,
            type_of_feedback: "Verbal",
            source_of_feedback: source,
            content_of_feedback: content,
            title,
            file_path: filePath
        });

        if (insertError) {
            console.error(insertError);
            setError("There has been an issue saving the feedback.");
            setMessage("");
            return;
        }

        // Create PDF
        const element = document.getElementById("feedbackPreview");
        const canvas = await html2canvas(element, { scale: 2 });
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4"
        });

        // PDF Dimensions
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 10;

        // Add Logo (optional)
        const logoImg = new Image();
        logoImg.src = logo;
        await new Promise(resolve => logoImg.onload = resolve); // Wait for logo to load

        const logoHeight = 20;
        const logoWidth = 40;
        const logoX = pageWidth - margin - logoWidth;
        pdf.addImage(logoImg, 'PNG', logoX, 10, logoWidth, logoHeight);

        // Add Header
        pdf.setFontSize(16);
        pdf.text("Verbal Feedback Summary", margin, margin + 10);

        pdf.setFontSize(12);
        pdf.text(`Date: ${date}`, margin, margin + 20);
        pdf.text(`Title: ${title}`, margin, margin + 32);
        pdf.text(`Source: ${source}`, margin, margin + 44, {maxWidth: 190});
        pdf.text(`Content: ${content}`, margin, margin + 56, {maxWidth: 190})

        // Convert to blob
        const pdfBlob = pdf.output("blob");

        // Upload to Supabase
        const { error: uploadError } = await supabase.storage
            .from('practice-feedback')
            .upload(filePath, pdfBlob, {
                cacheControl: '3600',
                upsert: true,
                contentType: 'application/pdf'
            });

        if (uploadError) {
            console.error(uploadError);
            setError("Feedback saved but PDF upload failed.");
        } else {
            setMessage("Verbal feedback saved.");
        }

        const { data } = await supabase
        .from('revalidation_documents')
        .insert({
            user_id: profile.id,
            document_type: 'Feedback',
            file_path: filePath,
            upload_date: new Date(),
            bucket: 'practice-feedback'
        });

        // Reset form
        setDate("");
        setSource("");
        setContent("");
        setTitle("");
        setError("");
        await refresh();
        };

    //Delete
    const handleDelete = async (feedbackEntry) => {
        const { id, file_path } = feedbackEntry;
        setUploading(true);
        setError("");
        setMessage("");

        try {
            if (file_path) {
                const { error: storageError } = await supabase.storage
                    .from("practice-feedback")
                    .remove([file_path]);

                if (storageError) {
                    throw new Error("Failed to delete file from storage.");
                }
            }

            const { error: dbError } = await supabase
                .from("practice_feedback")
                .delete()
                .eq("id", id);

            if (dbError) {
                throw new Error("Failed to delete feedback entry from database.");
            }

            const { error: tableError } = await supabase
                .from("revalidation_documents")
                .delete()
                .eq("file_path", file_path);

            if (tableError) {
                throw new Error("Failed to delete feedback entry from wider document database.");
            }

            setMessage("Feedback deleted successfully.");
            await refresh();
        } catch (err) {
            setError(err.message || "An unexpected error occurred.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div>
            <div classname="mob-only">
                <MobileRevalNav />
            </div>

            <div className="dashboard-body">
                <h1>PRACTICE FEEDBACK</h1>
                <p>You must have 5 pieces of practice-related feedback. You should not record any information that might identify an individual, whether the individual is alive or deceased. </p>

                <div className="feedback-upload">
                    <div className="upload-1 desktop-only">
                        <p style={{ fontWeight: "bold" }}>Photo Upload</p>
                        <input
                            id="fileInput"
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={handleFileSelectAndUpload}
                            style={{ display: "none" }}
                        />
                        <button className="reflective " onClick={handleButtonClick} disabled={uploading}>
                            <FontAwesomeIcon icon={faArrowUpFromBracket} size="2xl"/>
                            <br />
                            Upload Feedback
                        </button>
                    </div>

                    <div className="upload-1 mob-only">
                        <p style={{ fontWeight: "bold" }}>Photo Upload</p>
                        <input
                            id="fileInput"
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={handleFileSelectAndUpload}
                            style={{ display: "none" }}
                        />
                        <button className="reflective-mob " onClick={handleButtonClick} disabled={uploading}>
                            <FontAwesomeIcon icon={faArrowUpFromBracket} style={{marginRight: "1rem"}}/>
                            Upload Feedback
                        </button>
                    </div>

                    <div className="upload-2 mob-block">
                        <p style={{ fontWeight: "bold" }}>Verbal Feedback</p>
                        <form onSubmit={submitVerbalFeedback}>
                            <table className="feedback">
                                <tbody>
                                    <tr>
                                        <th>Date</th>
                                        <td><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></td>
                                    </tr>
                                    <tr>
                                        <th>Feedback Title</th>
                                        <td><input type="text" value={title} onChange={(e) => setTitle(e.target.value)} /></td>
                                    </tr>
                                    <tr>
                                        <th>Source of Feedback</th>
                                        <td><input type="text" value={source} onChange={(e) => setSource(e.target.value)} /></td>
                                    </tr>
                                    <tr>
                                        <th style={{ verticalAlign: "top" }}>Content of Feedback</th>
                                        <td><textarea value={content} onChange={(e) => setContent(e.target.value)} /></td>
                                    </tr>
                                </tbody>
                            </table>
                            <button className="reval" type="submit">Submit</button>
                        </form>
                    </div>
                </div>

                {error && <p className="error-message">{error}</p>}
                {message && <p className="success">{message}</p>}

                <div style={{ position: 'absolute', left: '-9999px', top: 0 }} id="feedbackPreview">
                    <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "14px" }}>
                        <tbody>
                            <tr><td><strong>Date:</strong></td><td>{date}</td></tr>
                            <tr><td><strong>Title:</strong></td><td>{title}</td></tr>
                            <tr><td><strong>Source:</strong></td><td>{source}</td></tr>
                            <tr><td><strong>Content:</strong></td><td>{content}</td></tr>
                        </tbody>
                    </table>
                </div>
                <br />
                <h3>YOUR UPLOADED FEEDBACK</h3>
                <p>NOTE: Please delete feedback that falls outside of your revalidation window.</p>
                <p>{feedback === 1
                    ? "You have uploaded 1 piece of practice-related feedback."
                    : `You have uploaded ${feedback.length} pieces of practice-related feedback.`}
                </p>
                <table className="reval-log">
                    <thead>
                        <tr>
                            <th>Feedback Title</th>
                            <th style={{width: "24%"}}>File Type</th>
                            <th style={{width: "24%"}}>Upload Date</th>
                            <th style={{width:"1rem", backgroundColor:"#fff"}}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {feedback.map((entry, index) => (
                            <tr key={index}>
                                <td>
                                    <a href={`https://dyuqmgoqgozadkxqnqqq.supabase.co/storage/v1/object/public/practice-feedback/${entry.file_path}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.9rem" }}>
                                        {entry.title}
                                    </a>
                                </td>
                                <td>{entry.type_of_feedback}</td>
                                <td>{new Date(entry.upload_date).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                })}</td>
                                <td>
                                    <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: "0.8rem" }} onClick={() => handleDelete(entry)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="mob-bottom-buffer">
        </div>
            </div>
        </div>
    );
}

export default FeedbackLog;